// src/components/MobileMenuBar.js
// src/components/MobileMenuBar.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { collection, query, getDocs, where, addDoc } from 'firebase/firestore'; // Added Firestore imports
import { db, auth } from '../firebase'; // Added Firebase config imports

import HolidayPlanner from './HolidayPlanner'; // Import the HolidayPlanner component
import './MobileMenuBar.css';

import calendarIcon from '../images/calendar.png';
import friendsIcon from '../images/friends.png';
import holidayIcon from '../images/holiday-icon.png';
import addFriendIcon from '../images/add_friend.png'; // Changed from gearIcon

// Destructure props that will be passed from Calendar.js
const MobileMenuBar = ({
  companies,
  patterns,
  shifts,
  selectedCompany,
  selectedPattern,
  selectedShift,
  handleCompanyChange,
  handlePatternChange,
  handleShiftChange, // Original handler from Calendar.js
  currentUser,
  friends, // New prop
  selectedFriend, // New prop
  handleFriendChange, // New prop
  onHolidayBooked // Prop from Calendar to refresh holidays
}) =>{
  const [showCalendarOptionsModal, setShowCalendarOptionsModal] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false); // State for Friends modal
  const [showHolidayPlannerModal, setShowHolidayPlannerModal] = useState(false); // State for Holiday Planner modal
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [showLoginRequiredModal, setShowLoginRequiredModal] = useState(false);

  // State for new Add Friend form (like AddPerson.js)
  const [nick, setNick] = useState('');
  const [formCompanies, setFormCompanies] = useState([]);
  const [selectedFormCompany, setSelectedFormCompany] = useState('');
  const [formPatterns, setFormPatterns] = useState({});
  const [selectedFormPattern, setSelectedFormPattern] = useState('');
  const [formShifts, setFormShifts] = useState([]);
  const [selectedFormShift, setSelectedFormShift] = useState('');
  const [addFriendError, setAddFriendError] = useState('');
  const [addFriendLoading, setAddFriendLoading] = useState(false);
  const [addFriendSuccess, setAddFriendSuccess] = useState(false);

  const handleCalendarOptionsClick = () => {
    setShowCalendarOptionsModal(true);
  };

  const handleCloseCalendarOptionsModal = () => {
    setShowCalendarOptionsModal(false);
  };

  // New handler for shift change within the modal
  const handleModalShiftSelectAndClose = (event) => {
    handleShiftChange(event); // Call the original handler to update state in Calendar.js
    handleCloseCalendarOptionsModal();
  };

  // Friends Modal Handlers
  const handleFriendsSelectionClick = () => setShowFriendsModal(true);
  const handleCloseFriendsModal = () => setShowFriendsModal(false);
  const handleModalFriendSelectAndClose = (event) => {
    handleFriendChange(event); // Call original handler from Calendar.js
    handleCloseFriendsModal(); // Close this modal
  };

  // Holiday Planner Modal Handlers
  const handleHolidayPlannerClick = () => {
    setShowHolidayPlannerModal(true);
  };
  const handleCloseHolidayPlannerModal = () => setShowHolidayPlannerModal(false);

  // Other Options Handler (placeholder)
  const handleAddFriendClick = () => {
    if (!currentUser) {
      setShowLoginRequiredModal(true);
    } else {
      // Reset form fields when opening
      setNick('');
      setSelectedFormCompany('');
      setSelectedFormPattern('');
      setSelectedFormShift('');
      setAddFriendError('');
      setAddFriendSuccess(false);
      // TODO: Fetch companies for the form here if not already loaded
      setShowAddFriendModal(true);
    }
  };

  const handleCloseAddFriendModal = () => {
    setShowAddFriendModal(false);
    // Clear form state
    setNick('');
    setSelectedFormCompany('');
    setSelectedFormPattern('');
    setSelectedFormShift('');
    setAddFriendError('');
    setAddFriendSuccess(false);
    // setFriendEmailToAdd(''); // No longer using this
  };

  const handleCloseLoginRequiredModal = () => {
    setShowLoginRequiredModal(false);
  };

  // Change handlers for the new Add Friend form
  const handleFormCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedFormCompany(company);
    if (formPatterns[company] && formPatterns[company].length > 0) {
      setSelectedFormPattern(formPatterns[company][0]);
    } else {
      setSelectedFormPattern('');
    }
    setSelectedFormShift(''); // Reset shift when company changes
    setFormShifts([]); // Clear previous shifts
  };

  const handleFormPatternChange = (e) => {
    setSelectedFormPattern(e.target.value);
    setSelectedFormShift(''); // Reset shift when pattern changes
    setFormShifts([]); // Clear previous shifts
  };

  // This will be the new handleSubmit for the AddFriendModal, adapted from AddPerson.js
  const handleSaveFriend = async () => {
    if (!nick || !selectedFormCompany || !selectedFormPattern || !selectedFormShift) {
      setAddFriendError('Please fill in all fields.');
      return;
    }

    setAddFriendError('');
    setAddFriendLoading(true);
    setAddFriendSuccess(false);

    try {
      const user = auth.currentUser; // Ensure we use the auth object imported
      if (!user) {
        setAddFriendError('User not authenticated. Please log in again.');
        setAddFriendLoading(false);
        return;
      }

      // Find the shift ID from shiftPatterns collection
      const shiftPatternsRef = collection(db, 'shiftPatterns');
      const q = query(
        shiftPatternsRef,
        where('company', '==', selectedFormCompany),
        where('patternName', '==', selectedFormPattern),
        where('shift', '==', selectedFormShift)
      );
      const querySnapshot = await getDocs(q);
      let shiftId = null;
      querySnapshot.forEach((doc) => {
        shiftId = doc.id; // Assuming the document ID is the shiftId we want
      });

      if (!shiftId) {
        setAddFriendError('Selected shift details could not be found. Please check selections.');
        setAddFriendLoading(false);
        return;
      }

      // Add friend to the current user's 'friends' subcollection
      await addDoc(collection(db, 'users', user.uid, 'friends'), {
        nick: nick,
        company: selectedFormCompany,
        pattern: selectedFormPattern, // This is patternName in AddPerson.js
        shift: selectedFormShift,     // This is shift (name) in AddPerson.js
        shiftId: shiftId              // The document ID from shiftPatterns
      });

      setAddFriendSuccess(true);
      setTimeout(() => {
        handleCloseAddFriendModal();
      }, 2000); // Close modal after 2 seconds on success

    } catch (error) {
      console.error('Error adding friend in MobileMenuBar:', error);
      setAddFriendError('Failed to add friend. ' + error.message);
    } finally {
      setAddFriendLoading(false);
    }
  };

  // useEffect for fetching companies and patterns for the Add Friend form
  useEffect(() => {
    const fetchCompaniesAndPatternsForForm = async () => {
      if (!showAddFriendModal || !currentUser) return; // Only fetch if modal is open and user is logged in
      setAddFriendLoading(true);
      try {
        const shiftsRef = collection(db, 'shiftPatterns');
        const querySnapshot = await getDocs(shiftsRef);
        const uniqueCompanies = [];
        const companyPatterns = {};
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          const companyName = data.company;
          const patternName = data.patternName;
          if (companyName && !uniqueCompanies.includes(companyName)) {
            uniqueCompanies.push(companyName);
          }
          if (companyName && patternName) {
            if (!companyPatterns[companyName]) {
              companyPatterns[companyName] = [];
            }
            if (!companyPatterns[companyName].includes(patternName)) {
              companyPatterns[companyName].push(patternName);
            }
          }
        });
        setFormCompanies(uniqueCompanies);
        setFormPatterns(companyPatterns);
        if (uniqueCompanies.length > 0 && !selectedFormCompany) {
           // setSelectedFormCompany(uniqueCompanies[0]); // Optionally pre-select
        }
      } catch (error) {
        console.error("Error fetching companies for form:", error);
        setAddFriendError("Failed to load company data.");
      } finally {
        setAddFriendLoading(false);
      }
    };
    fetchCompaniesAndPatternsForForm();
  }, [showAddFriendModal, currentUser, selectedFormCompany]); // Re-fetch if modal visibility or user changes, or company changes

  // useEffect for fetching shifts for the Add Friend form based on company and pattern selection
  useEffect(() => {
    const fetchShiftsForForm = async () => {
      if (!selectedFormCompany || !selectedFormPattern || !showAddFriendModal || !currentUser) {
        setFormShifts([]); // Clear shifts if no company/pattern or modal closed
        return;
      }
      setAddFriendLoading(true);
      try {
        const shiftsRef = collection(db, 'shiftPatterns');
        const q = query(
          shiftsRef,
          where('company', '==', selectedFormCompany),
          where('patternName', '==', selectedFormPattern)
        );
        const querySnapshot = await getDocs(q);
        const shiftsData = [];
        querySnapshot.forEach((doc) => {
          shiftsData.push({ id: doc.id, ...doc.data() });
        });
        setFormShifts(shiftsData);
        if (shiftsData.length > 0 && !selectedFormShift) {
          // setSelectedFormShift(shiftsData[0].shift); // Optionally pre-select
        }
      } catch (error) {
        console.error("Error fetching shifts for form:", error);
        setAddFriendError("Failed to load shift data.");
      } finally {
        setAddFriendLoading(false);
      }
    };
    fetchShiftsForForm();
  }, [selectedFormCompany, selectedFormPattern, showAddFriendModal, currentUser, selectedFormShift]);

  return (
    <>
      {/* Calendar Options Modal */}
      <Modal
        show={showCalendarOptionsModal} 
        onHide={handleCloseCalendarOptionsModal} 
        centered 
        dialogClassName="glassmorphism-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Calendar & Shift Options</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Company</Form.Label>
            <Form.Select value={selectedCompany} onChange={handleCompanyChange}>
              {Array.isArray(companies) && companies.map((company, index) => (
                <option key={index} value={company}>{company}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Pattern</Form.Label>
            <Form.Select value={selectedPattern} onChange={handlePatternChange} disabled={!selectedCompany || !patterns || !patterns[selectedCompany]}>
              {selectedCompany && patterns && Array.isArray(patterns[selectedCompany]) &&
                patterns[selectedCompany].map((pattern, index) => (
                  <option key={index} value={pattern}>{pattern}</option>
                ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Shift</Form.Label>
            <Form.Select value={selectedShift} onChange={handleModalShiftSelectAndClose} disabled={!shifts || shifts.length === 0}>
              {Array.isArray(shifts) && shifts.map((shift, index) => (
                <option key={index} value={shift.shift}>{shift.shift}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCalendarOptionsModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Friends Selection Modal */}
      <Modal 
        show={showFriendsModal} 
        onHide={handleCloseFriendsModal} 
        centered 
        dialogClassName="glassmorphism-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Friend</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select Person</Form.Label>
            <Form.Select value={selectedFriend ? selectedFriend.id : ''} onChange={handleModalFriendSelectAndClose} disabled={!friends || friends.length === 0}>
              
              <option value="">Your Plan</option>
              {Array.isArray(friends) && friends.map((friend) => (
                <option key={friend.id} value={friend.id}>{friend.nick}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseFriendsModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Holiday Planner Modal */}
      <Modal 
        show={showHolidayPlannerModal} 
        onHide={handleCloseHolidayPlannerModal} 
        centered 
        dialogClassName="glassmorphism-modal" 
        contentClassName="holiday-planner-modal-container" /* Changed for potentially wider content */
        size="lg" /* Optional: make modal larger for planner */
      >
        <Modal.Header closeButton>
          <Modal.Title>Holiday Planner</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: 0}}> {/* Remove padding if HolidayPlanner component handles its own */} 
          {/* HolidayPlanner component now handles its own stages and content */}
          <HolidayPlanner 
            currentUser={currentUser} 
            onClose={handleCloseHolidayPlannerModal} // Pass the close handler
            onHolidayBooked={onHolidayBooked} // Pass the refresh callback
            initialSelectedPattern={selectedPattern}
            initialSelectedShift={selectedShift}
            onCompanyChange={handleCompanyChange} // Pass down the handlers from Calendar.js via MobileMenuBar
            onPatternChange={handlePatternChange}
            onShiftChange={handleShiftChange} // Pass down the original shift change handler
          />
        </Modal.Body>
        {/* Footer is removed as HolidayPlanner now contains its own navigation/action buttons */}
      </Modal>

      {/* Add Friend Modal (New Form) */}
      <Modal 
        show={showAddFriendModal} 
        onHide={handleCloseAddFriendModal} 
        centered 
        dialogClassName="glassmorphism-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Person Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {addFriendError && <div className="alert alert-danger">{addFriendError}</div>}
          {addFriendSuccess && <div className="alert alert-success">Friend added successfully!</div>}
          <Form onSubmit={(e) => { e.preventDefault(); handleSaveFriend(); }}>
            <Form.Group className="mb-3" controlId="formFriendNick">
              <Form.Label>Nickname</Form.Label>
              <Form.Control 
                type="text" 
                placeholder="Enter nickname" 
                value={nick}
                onChange={(e) => setNick(e.target.value)} 
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formFriendCompany">
              <Form.Label>Company</Form.Label>
              <Form.Select 
                aria-label="Select company"
                value={selectedFormCompany}
                onChange={handleFormCompanyChange}
                required
              >
                <option value="">Select Company</option>
                {formCompanies.map(company => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formFriendPattern">
              <Form.Label>Pattern</Form.Label>
              <Form.Select 
                aria-label="Select pattern"
                value={selectedFormPattern}
                onChange={handleFormPatternChange}
                disabled={!selectedFormCompany || !formPatterns[selectedFormCompany]}
                required
              >
                <option value="">Select Pattern</option>
                {selectedFormCompany && formPatterns[selectedFormCompany] && formPatterns[selectedFormCompany].map(pattern => (
                  <option key={pattern} value={pattern}>{pattern}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="formFriendShift">
              <Form.Label>Shift</Form.Label>
              <Form.Select 
                aria-label="Select shift"
                value={selectedFormShift}
                onChange={(e) => setSelectedFormShift(e.target.value)}
                disabled={!selectedFormPattern || formShifts.length === 0}
                required
              >
                <option value="">Select Shift</option>
                {formShifts.map(shiftObj => (
                  <option key={shiftObj.id} value={shiftObj.shift}>{shiftObj.shift} (Starts: {shiftObj.startTime})</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddFriendModal} disabled={addFriendLoading}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveFriend} disabled={addFriendLoading || addFriendSuccess}>
            {addFriendLoading ? 'Saving...' : (addFriendSuccess ? 'Saved!' : 'Save Friend')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* The actual menu bar buttons */}
      <div className="mobile-menu-bar">
        <button onClick={handleCalendarOptionsClick} className="menu-button" aria-label="Calendar Options">
          <img src={calendarIcon} alt="Calendar Options" />
        </button>
        <button onClick={handleFriendsSelectionClick} className="menu-button" aria-label="Friends Selection">
          <img src={friendsIcon} alt="Friends Selection" />
        </button>
        <button onClick={handleHolidayPlannerClick} className="menu-button" aria-label="Holiday Planner">
          <img src={holidayIcon} alt="Holiday Planner" />
        </button>
        <button onClick={handleAddFriendClick} className="menu-button" aria-label="Add Friend">
          <img src={addFriendIcon} alt="Add Friend" />
        </button>
      </div>

      {/* Login Required Modal */}
      <Modal show={showLoginRequiredModal} onHide={handleCloseLoginRequiredModal} centered dialogClassName="glassmorphism-modal">
        <Modal.Header closeButton>
          <Modal.Title>Login Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You need to be logged in to add a friend.</p>
          <p>Please log in or register to continue.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseLoginRequiredModal}>Close</Button>
          {/* Optional: Add direct links to login/register pages */}
          {/* <Button variant="primary" as={Link} to="/login">Login</Button> */}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MobileMenuBar;
